/**
 * Catálogo declarativo de todos los proveedores externos de Structura.
 *
 * Cada entrada define automáticamente:
 *   nombre, logo, descripción, categoría, capability, feature flag,
 *   plan mínimo, estado, documentación y componente de configuración.
 *
 * Para agregar un proveedor nuevo: solo registrar aquí. La UI se adapta sola.
 */
import { lazy } from "react";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import type { LucideIcon } from "lucide-react";
import {
  FileCheck2, ShieldCheck, CreditCard, Truck, MessageSquare,
  BarChart3, Monitor, Globe, Zap, Package, ChevronRight,
} from "lucide-react";

// ─── Types ────────────────────────────────────────────────────────────────────

export type ProviderCategory =
  | "invoicing"
  | "payments"
  | "shipping"
  | "messaging"
  | "analytics"
  | "hardware";

export type ProviderStatus =
  | "configured"
  | "not_configured"
  | "requires_credentials"
  | "requires_plan"
  | "sandbox"
  | "production"
  | "disabled"
  | "error"
  | "unavailable"
  | "deprecated"
  | "beta"
  | "coming_soon";

export type ProviderTag = "sandbox" | "beta" | "coming_soon" | "deprecated";

export interface ProviderDescriptor {
  id: string;
  name: string;
  shortName?: string;
  icon?: LucideIcon;
  emoji?: string;
  description: string;
  category: ProviderCategory;
  type?: string;
  flagKey?: string;
  minPlan?: "starter" | "pro" | "business";
  docsUrl?: string;
  ConfigPanel?: React.LazyExoticComponent<React.ComponentType>;
  tags?: ProviderTag[];
}

// ─── HOC — envuelve paneles que reciben tenantId como prop ────────────────────

function makeTenantPanel(
  panelImport: () => Promise<{ default: React.ComponentType<{ tenantId: string }> }>,
): React.LazyExoticComponent<React.ComponentType> {
  return lazy(
    async (): Promise<{ default: React.ComponentType }> => {
      const { default: Panel } = await panelImport();
      function WrappedPanel() {
        const tid = useWorkspaceTenantId();
        if (!tid) return null;
        return <Panel tenantId={tid} />;
      }
      return { default: WrappedPanel };
    },
  );
}

// ─── Registro ─────────────────────────────────────────────────────────────────

export const PROVIDER_REGISTRY: Record<string, ProviderDescriptor> = {
  // ── FACTURACIÓN ────────────────────────────────────────────────────────────
  tusfacturas: {
    id: "tusfacturas",
    name: "TusFacturasAPP",
    icon: FileCheck2,
    description: "Facturación electrónica AFIP/ARCA vía API. Compatible con Facturas A, B, C y Notas de crédito. BYO API key.",
    category: "invoicing",
    type: "API key propio",
    flagKey: "invoicing_tusfacturas",
    minPlan: "pro",
    docsUrl: "https://www.tusfacturas.com.ar/documentacion-api",
    ConfigPanel: lazy(() =>
      import("@/components/settings/TusFacturasPanel").then((m) => ({ default: m.TusFacturasPanel }))
    ),
  },
  arca_directo: {
    id: "arca_directo",
    name: "ARCA Directo",
    shortName: "ARCA",
    icon: ShieldCheck,
    description: "Conexión directa con ARCA/AFIP usando certificado fiscal. Sin intermediarios. Compatible con CAEA y CAE en línea.",
    category: "invoicing",
    type: "Certificado fiscal",
    flagKey: "invoicing_afip_direct",
    minPlan: "business",
    docsUrl: "https://www.afip.gob.ar/ws/documentacion",
    ConfigPanel: lazy(() =>
      import("@/components/settings/AfipDirectPanel").then((m) => ({ default: m.AfipDirectPanel }))
    ),
  },

  // ── PAGOS ──────────────────────────────────────────────────────────────────
  mercadopago: {
    id: "mercadopago",
    name: "Mercado Pago",
    icon: CreditCard,
    description: "Pasarela de pago líder en Argentina. Acepta tarjetas, débito, QR, cuotas y billetera digital.",
    category: "payments",
    type: "OAuth",
    flagKey: "payment_gateway_mp",
    docsUrl: "https://www.mercadopago.com.ar/developers",
    ConfigPanel: lazy(() =>
      import("@/components/settings/PaymentGatewaySettingsPanel").then((m) => ({ default: m.PaymentGatewaySettingsPanel as React.ComponentType }))
    ),
  },
  payway: {
    id: "payway",
    name: "Payway",
    icon: CreditCard,
    description: "Procesador de pagos con tarjetas. Compatible con débito y crédito de todos los bancos.",
    category: "payments",
    type: "API key",
    flagKey: "payment_gateway_payway",
    docsUrl: "https://payway.com.ar/desarrolladores",
    ConfigPanel: lazy(() =>
      import("@/components/settings/PaymentGatewaySettingsPanel").then((m) => ({ default: m.PaymentGatewaySettingsPanel as React.ComponentType }))
    ),
  },
  getnet: {
    id: "getnet",
    name: "Getnet",
    icon: CreditCard,
    description: "Pasarela bancaria de Santander. Tarjetas nacionales e internacionales.",
    category: "payments",
    type: "API key",
    flagKey: "payment_gateway_getnet",
    tags: ["beta"],
    ConfigPanel: lazy(() =>
      import("@/components/settings/PaymentGatewaySettingsPanel").then((m) => ({ default: m.PaymentGatewaySettingsPanel as React.ComponentType }))
    ),
  },
  uala: {
    id: "uala",
    name: "Ualá",
    icon: CreditCard,
    description: "Billetera digital y pasarela de pagos. QR y link de pago.",
    category: "payments",
    type: "API key",
    flagKey: "payment_gateway_uala",
    tags: ["beta"],
    ConfigPanel: lazy(() =>
      import("@/components/settings/PaymentGatewaySettingsPanel").then((m) => ({ default: m.PaymentGatewaySettingsPanel as React.ComponentType }))
    ),
  },
  // ── ENVÍOS ─────────────────────────────────────────────────────────────────
  shipnow: {
    id: "shipnow",
    name: "ShipNow",
    icon: Truck,
    description: "Logística multi-carrier. Cotización automática con todos los couriers del mercado.",
    category: "shipping",
    type: "API key",
    flagKey: "ship_pap",
    docsUrl: "https://developers.shipnow.com.ar",
    ConfigPanel: makeTenantPanel(() =>
      import("@/components/settings/ShipnowLogisticsPanel").then((m) => ({ default: m.ShipnowLogisticsPanel }))
    ),
  },
  treggo: {
    id: "treggo",
    name: "Treggo",
    icon: Truck,
    description: "Última milla y same-day delivery. Entregas el mismo día en CABA y GBA.",
    category: "shipping",
    type: "API key",
    flagKey: "ship_treggo",
    docsUrl: "https://treggo.co/desarrolladores",
    ConfigPanel: makeTenantPanel(() =>
      import("@/components/settings/TreggoSettingsPanel").then((m) => ({ default: m.TreggoSettingsPanel }))
    ),
  },
  andreani: {
    id: "andreani",
    name: "Andreani",
    icon: Truck,
    description: "Envíos a todo el país. Cobertura nacional con seguimiento en tiempo real.",
    category: "shipping",
    type: "API key",
    flagKey: "ship_andreani",
    docsUrl: "https://developers.andreani.com",
    ConfigPanel: makeTenantPanel(() =>
      import("@/components/settings/AndreaniSettingsPanel").then((m) => ({ default: m.AndreaniSettingsPanel }))
    ),
  },
  cabify: {
    id: "cabify",
    name: "Cabify",
    icon: Truck,
    description: "Envíos urbanos con vehículos on-demand. Ideal para entregas express en ciudades.",
    category: "shipping",
    type: "API key",
    flagKey: "ship_cabify",
    ConfigPanel: makeTenantPanel(() =>
      import("@/components/settings/CabifySettingsPanel").then((m) => ({ default: m.CabifySettingsPanel }))
    ),
  },
  zipnova: {
    id: "zipnova",
    name: "Zipnova",
    icon: Truck,
    description: "Logística express en CABA y GBA. Entregas en 2 horas.",
    category: "shipping",
    type: "API key",
    flagKey: "ship_zipnova",
    ConfigPanel: makeTenantPanel(() =>
      import("@/components/settings/ZipnovaSettingsPanel").then((m) => ({ default: m.ZipnovaSettingsPanel }))
    ),
  },
  pickit: {
    id: "pickit",
    name: "Pickit",
    icon: Package,
    description: "Red de puntos de retiro y entrega en negocios de barrio. Ideal para e-commerce.",
    category: "shipping",
    type: "API key",
    flagKey: "ship_pickit",
    docsUrl: "https://pickit.net.ar/developers",
    ConfigPanel: makeTenantPanel(() =>
      import("@/components/settings/PickitSettingsPanel").then((m) => ({ default: m.PickitSettingsPanel }))
    ),
  },
  pedidosya: {
    id: "pedidosya",
    name: "PedidosYa Envíos",
    icon: Truck,
    description: "Logística de PedidosYa para negocios. Entregas urbanas con tracking en tiempo real.",
    category: "shipping",
    type: "API key",
    flagKey: "ship_pedidosya",
    ConfigPanel: makeTenantPanel(() =>
      import("@/components/settings/PedidosYaSettingsPanel").then((m) => ({ default: m.PedidosYaSettingsPanel }))
    ),
  },
  rapiboy: {
    id: "rapiboy",
    name: "Rapiboy",
    icon: Truck,
    description: "Mensajería y delivery express en CABA. Motoqueros en minutos.",
    category: "shipping",
    type: "API key",
    flagKey: "ship_rapiboy",
    ConfigPanel: makeTenantPanel(() =>
      import("@/components/settings/RapiboySettingsPanel").then((m) => ({ default: m.RapiboySettingsPanel }))
    ),
  },
  oca: {
    id: "oca",
    name: "OCA",
    icon: Truck,
    description: "Correo privado con cobertura nacional. Envíos normales y express con sucursales.",
    category: "shipping",
    type: "API key",
    flagKey: "ship_oca",
    docsUrl: "https://www.oca.com.ar/developers",
    ConfigPanel: makeTenantPanel(() =>
      import("@/components/settings/OcaSettingsPanel").then((m) => ({ default: m.OcaSettingsPanel }))
    ),
  },
  correo_argentino: {
    id: "correo_argentino",
    name: "Correo Argentino",
    icon: Truck,
    description: "Servicio postal estatal con cobertura en todo el territorio nacional.",
    category: "shipping",
    type: "API key",
    flagKey: "ship_correo",
    ConfigPanel: makeTenantPanel(() =>
      import("@/components/settings/CorreoArgentinoSettingsPanel").then((m) => ({ default: m.CorreoArgentinoSettingsPanel }))
    ),
  },
  uber_direct: {
    id: "uber_direct",
    name: "Uber Direct",
    icon: Truck,
    description: "Entregas on-demand con la red de Uber. Disponible en principales ciudades.",
    category: "shipping",
    type: "API key",
    flagKey: "ship_uber_direct",
    tags: ["beta"],
    ConfigPanel: makeTenantPanel(() =>
      import("@/components/settings/UberDirectSettingsPanel").then((m) => ({ default: m.UberDirectSettingsPanel }))
    ),
  },
  entrega_propia: {
    id: "entrega_propia",
    name: "Entrega propia",
    icon: Truck,
    description: "Configurá tu propia flota de delivery con zonas, precios y horarios personalizados.",
    category: "shipping",
    type: "Interno",
  },

  // ── MENSAJERÍA ─────────────────────────────────────────────────────────────
  whatsapp: {
    id: "whatsapp",
    name: "WhatsApp",
    icon: MessageSquare,
    description: "Notificaciones transaccionales por WhatsApp. Confirmaciones de pedido, seguimiento y alertas.",
    category: "messaging",
    type: "API key",
    flagKey: "whatsapp_notify",
    docsUrl: "https://developers.facebook.com/docs/whatsapp",
    ConfigPanel: lazy(() =>
      import("@/components/settings/TenantMessagingPanel").then((m) => ({ default: m.TenantMessagingPanel }))
    ),
  },
  kapso: {
    id: "kapso",
    name: "Kapso",
    icon: MessageSquare,
    description: "Plataforma de WhatsApp Business API nativa para Argentina. Soporte multiagente.",
    category: "messaging",
    type: "API key",
    tags: ["coming_soon"],
  },
  resend: {
    id: "resend",
    name: "Resend",
    icon: MessageSquare,
    description: "Email transaccional para developers. API moderna, alta deliverability.",
    category: "messaging",
    type: "API key",
    tags: ["coming_soon"],
  },
  push_notifications: {
    id: "push_notifications",
    name: "Push Notifications",
    icon: Zap,
    description: "Notificaciones push web y PWA. Mantené a tus clientes informados en tiempo real.",
    category: "messaging",
    tags: ["coming_soon"],
  },

  // ── ANALYTICS ─────────────────────────────────────────────────────────────
  google_analytics: {
    id: "google_analytics",
    name: "Google Analytics",
    shortName: "GA4",
    icon: BarChart3,
    description: "Analítica web avanzada. Seguimiento de conversiones, embudos y audiencias.",
    category: "analytics",
    type: "Script",
    docsUrl: "https://analytics.google.com",
  },
  meta_pixel: {
    id: "meta_pixel",
    name: "Meta Pixel",
    shortName: "Pixel",
    icon: BarChart3,
    description: "Seguimiento de conversiones para campañas de Facebook e Instagram Ads.",
    category: "analytics",
    type: "Script",
    docsUrl: "https://developers.facebook.com/docs/meta-pixel",
  },
  tiktok_pixel: {
    id: "tiktok_pixel",
    name: "TikTok Pixel",
    icon: BarChart3,
    description: "Medición de conversiones para campañas de TikTok Ads.",
    category: "analytics",
    type: "Script",
    tags: ["beta"],
  },
  clarity: {
    id: "clarity",
    name: "Microsoft Clarity",
    shortName: "Clarity",
    icon: BarChart3,
    description: "Grabaciones de sesiones y mapas de calor gratuitos.",
    category: "analytics",
    type: "Script",
    tags: ["coming_soon"],
  },
  hotjar: {
    id: "hotjar",
    name: "Hotjar",
    icon: BarChart3,
    description: "Heatmaps, grabaciones y encuestas para entender el comportamiento de usuarios.",
    category: "analytics",
    type: "Script",
    tags: ["coming_soon"],
  },

  // ── HARDWARE ──────────────────────────────────────────────────────────────
  qz_tray: {
    id: "qz_tray",
    name: "QZ Tray",
    icon: Monitor,
    description: "Driver de impresión para ticketeras, etiquetadoras y comanderas. USB y red.",
    category: "hardware",
    type: "Desktop app",
    tags: ["coming_soon"],
  },
  mp_point: {
    id: "mp_point",
    name: "Mercado Pago Point",
    icon: Monitor,
    description: "Terminal de pago física con chip, banda y contactless. Integrada con el POS.",
    category: "hardware",
    type: "Hardware",
    tags: ["coming_soon"],
  },
  payway_smart: {
    id: "payway_smart",
    name: "Payway Smart",
    icon: Monitor,
    description: "Terminal POS inteligente de Payway con pantalla táctil y Android integrado.",
    category: "hardware",
    type: "Hardware",
    tags: ["coming_soon"],
  },
} as const;

// ─── Helpers ──────────────────────────────────────────────────────────────────

export const CATEGORY_LABELS: Record<ProviderCategory, string> = {
  invoicing: "Facturación",
  payments: "Pagos",
  shipping: "Envíos",
  messaging: "Mensajería",
  analytics: "Analytics",
  hardware: "Hardware",
};

export const CATEGORY_ICONS: Record<ProviderCategory, LucideIcon> = {
  invoicing: FileCheck2,
  payments: CreditCard,
  shipping: Truck,
  messaging: MessageSquare,
  analytics: BarChart3,
  hardware: Monitor,
};

export function resolveProviderStatus(
  descriptor: ProviderDescriptor,
  productFlags?: Record<string, boolean | undefined>,
): ProviderStatus {
  if (descriptor.tags?.includes("coming_soon")) return "coming_soon";
  if (descriptor.tags?.includes("deprecated")) return "deprecated";
  if (descriptor.tags?.includes("beta")) {
    if (descriptor.flagKey && productFlags?.[descriptor.flagKey]) return "configured";
    return "beta";
  }
  if (!descriptor.flagKey) return "not_configured";
  if (productFlags?.[descriptor.flagKey]) return "configured";
  if (descriptor.minPlan) return "requires_plan";
  return "not_configured";
}

export function getProviderDescriptors(ids: string[]): ProviderDescriptor[] {
  return ids.map((id) => PROVIDER_REGISTRY[id]).filter(Boolean);
}

// Needed to suppress unused import warning from lucide
void ChevronRight;
void Globe;
